import {
  Box,
  Button,
  Fab,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEvent, useCallback, useState } from 'react'
import { useCopyToClipboard } from 'usehooks-ts'
import { IconButton, Icons, InformationModal, SkeletonText } from 'utoniq-ui'

import {
  ConsoleCollectionDetailFragment,
  ConsoleCollectionDetailQuery,
  ConsoleCollectionItemsConnectionFragment,
  CryptoWallet,
  EventBoxFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { Head, HeaderImageArea } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  CollectionPublishedLabel,
  ItemSelectBox,
  ItemSelectBoxSkeleton,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  CryptoWalletConnectDrawer,
  CryptoWalletConnectDrawerProps,
} from '@/components/organisms/nft'
import { EventModal } from '@/components/pages/ConsoleCollectionDetail/template/EventModal'
import {
  ItemSelectModal,
  ItemSelectModalProps,
} from '@/components/pages/ConsoleCollectionDetail/template/ItemSelectModal'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { config } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { checkMetamaskInstalled } from '@/core/web3/metamask'
import { generateFullPath } from '@/lib/url'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  fetching: boolean
  hasNextPage: boolean
  removingItem: boolean
  removingCollection: boolean
  collectionId: string
  collection: Maybe<ConsoleCollectionDetailQuery['collection']>
  itemSelectProps: Pick<
    ItemSelectModalProps,
    'loading' | 'hasNextPage' | 'items' | 'addingItem'
  >
  walletConnecting: boolean
  isNft: boolean
  nftIssuing: boolean
  artistWalletAddress?: Maybe<CryptoWallet['address']>
  event: Maybe<EventBoxFragment> | undefined
}

export type Action = {
  onFetchMore: () => Promise<void>
  onRemove: () => Promise<void>
  onSelectItems: ItemSelectModalProps['onSelect']
  onSearchItems: ItemSelectModalProps['onSearch']
  onFetchMoreItems: ItemSelectModalProps['onFetchMore']
  onConnectMetamask: () => Promise<void>
  onConnectWeb3auth: () => Promise<any>
  onToggleActive: (id: string, isActive: boolean) => Promise<void>
  onToggleSelling: (id: string, isSelling: boolean) => Promise<void>
  onRemoveItemFromCollection: (itemId: string) => Promise<void>
}

export const ConsoleCollectionDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onRemove,
  onSelectItems,
  onSearchItems,
  onFetchMoreItems,
  onConnectMetamask,
  onConnectWeb3auth,
  onToggleActive,
  onToggleSelling,
  onRemoveItemFromCollection,
}) => {
  const { t } = useTranslation('consoleCollectionDetail')
  const { router, routerNames, generatePath, routerPages } = useRouter()
  const { showMessage, showError } = useMessage()
  const [_, copy] = useCopyToClipboard()
  const { onClick } = useBrowserLink(
    generateFullPath(routerPages.CollectionDetail, {
      collectionId: state.collection?.id ?? '#',
    })
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openTrashConfirm, setOpenTrashConfirm] = useState<boolean>(false)

  const options = [
    {
      label: t('openStoreLabel'),
      icon: (
        <ListItemIcon sx={{ '&.MuiListItemIcon-root': { minWidth: 24 } }}>
          <Icons.Window fontSize="xSmall" />
        </ListItemIcon>
      ),
      onClick() {
        onClick()
        setAnchorEl(null)
      },
    },
    {
      label: t('copyURLLabel'),
      async onClick() {
        await copy(
          generateFullPath(routerPages.CollectionDetail, {
            collectionId: state.collection?.id,
          })
        )
        showMessage(t('copyURLSuccess'), 'success')
        setAnchorEl(null)
      },
    },
    {
      label: t('edit'),
      onClick() {
        handleClose()
        router.push(
          generatePath(routerPages.ConsoleCollectionEdit, {
            collectionId: state.collection?.id,
          })
        )
      },
    },
    {
      label: t('remove'),
      onClick() {
        handleClose()
        setOpenTrashConfirm(true)
      },
    },
  ]

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleCollectionDetail} />
      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleCollections,
          title: state.collection?.title,
          right: (
            <IconButton.More
              edge="end"
              size="medium"
              onClick={handleClick}
              color="white"
            />
          ),
        }}
      >
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {options.map(option => {
            if (
              config.modules.nft &&
              state.collection?.nft &&
              option.label === t('remove')
            )
              return null
            return (
              <MenuItem
                key={option.label}
                onClick={option.onClick}
                sx={{ width: 200 }}
              >
                {option.icon}
                {option.label}
              </MenuItem>
            )
          })}
        </Menu>
        {state.loading ? (
          <CollectionDetailLoading />
        ) : (
          <>
            <BannerCell thumbnail={state.collection?.thumbnail ?? null} />
            <CollectionOverviewTextCell
              title={state.collection?.title ?? ''}
              description={state.collection?.description ?? ''}
              artist={state.collection?.artist ?? null}
              status={state.collection?.status}
              event={state.event}
              formattedStartedAt={state.collection?.formattedStartedAt}
              formattedEndedAt={state.collection?.formattedEndedAt}
            />
          </>
        )}
        {config.modules.nft && state.collection?.nft && (
          <NftTextCell nft={state.collection.nft} />
        )}
        <ItemImageListCell
          artistId={state.artistId}
          loading={state.fetching || state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          collectionItems={state.collection?.collectionItems ?? null}
          onToggleActive={onToggleActive}
          onToggleSelling={onToggleSelling}
          onRemoveItemFromCollection={onRemoveItemFromCollection}
        />
        {!state.loading && (
          <BaseCell
            artistId={state.artistId}
            {...state.itemSelectProps}
            values={
              state.collection?.collectionItems?.edges.map(
                ({ node }) => node.item.id
              ) ?? []
            }
            onFetchMore={onFetchMoreItems}
            onSelect={onSelectItems}
            onSearch={onSearchItems}
            walletConnecting={state.walletConnecting}
            isNft={state.isNft}
            nft={state.collection?.nft}
            nftIssuing={state.nftIssuing}
            hasCryptoWallet={!!state.artistWalletAddress}
            onConnectWallet={walletType => {
              if (walletType === 'metamask') {
                try {
                  checkMetamaskInstalled()
                } catch (e) {
                  showError(e as Error)
                }
                return onConnectMetamask().then(() =>
                  showMessage(t('connectedWallet'))
                )
              }

              if (walletType === 'web3auth') {
                return onConnectWeb3auth().then(() =>
                  showMessage(t('connectedWallet'))
                )
              }
            }}
          />
        )}
        <InformationModal
          open={openTrashConfirm}
          onClose={() => setOpenTrashConfirm(false)}
          title={t('trashConfirmTitle')}
          content={t('trashConfirmDesc')}
          cancelText={t('cancel')}
          buttons={
            <Button
              sx={{ minWidth: 212 }}
              variant="contained"
              color="primary"
              size="large"
              loading={state.removingCollection}
              onClick={onRemove}
            >
              {t('delete')}
            </Button>
          }
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type BannerCellProps = {
  thumbnail: Maybe<ConsoleCollectionDetailFragment['thumbnail']>
}
const BannerCell: Cell<BannerCellProps> = ({ thumbnail }) => {
  const thumbnailUri = thumbnail?.mediumUri ?? thumbnail?.uri ?? ''
  return <HeaderImageArea imageUri={thumbnailUri} />
}

type CollectionOverviewTextCellProps = {
  artist: Maybe<ConsoleCollectionDetailFragment['artist']>
  title: ConsoleCollectionDetailFragment['title']
  event: State['event']
  description: ConsoleCollectionDetailFragment['description']
  status?: ConsoleCollectionDetailFragment['status']
  formattedStartedAt?: ConsoleCollectionDetailFragment['formattedStartedAt']
  formattedEndedAt?: ConsoleCollectionDetailFragment['formattedEndedAt']
}
const CollectionOverviewTextCell: Cell<CollectionOverviewTextCellProps> = ({
  artist,
  title,
  event,
  description,
  status,
  formattedStartedAt,
  formattedEndedAt,
}) => {
  const { t } = useTranslation('consoleCollectionDetail')
  const [open, setOpen] = useState<boolean>(false)

  return (
    <CellFrame sx={{ p: 5 }}>
      <Typography variant="subtitle2" color="text.hint">
        {artist?.displayName}
      </Typography>
      <Typography variant="h3" sx={{ pt: 1 }}>
        {title}
      </Typography>
      <Stack sx={{ pt: 3 }}>
        <CollectionPublishedLabel
          status={status}
          formattedStartedAt={formattedStartedAt}
          formattedEndedAt={formattedEndedAt}
        />
      </Stack>
      <Typography variant="body2" sx={{ pt: 3 }}>
        {description}
      </Typography>
      {!!event && (
        <>
          <Box sx={{ pt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setOpen(true)}
              sx={{ borderColor: 'primary.main', color: 'primary.main' }}
            >
              {t('eventListNum')}
            </Button>
          </Box>
          <EventModal
            event={event}
            open={open}
            onClose={() => setOpen(false)}
          />
        </>
      )}
    </CellFrame>
  )
}

type NftTextCellProps = {
  nft: ConsoleCollectionDetailFragment['nft']
}
const NftTextCell: Cell<NftTextCellProps> = ({ nft }) => {
  const { t } = useTranslation('consoleCollectionDetail')
  return (
    <CellFrame sx={{ px: 5, pb: 5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Icons.Nft fontSize="small" filledColor />
        <Typography variant="h6">{t('nftSetting')}</Typography>
      </Stack>
      <Stack direction="row" sx={{ pt: 2 }}>
        <Stack sx={{ width: 85 }} spacing={1}>
          <Typography variant="subtitle1" color="text.secondary">
            {t('howToSell')}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t('royalty')}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t('chain')}
          </Typography>
        </Stack>
        <Stack flex={1} spacing={1}>
          <Typography variant="subtitle1">{t(nft?.salesType ?? '')}</Typography>
          <Typography variant="subtitle1">
            {nft?.chains?.[0]?.royalty ?? 0}%
          </Typography>
          <Typography variant="subtitle1">
            {nft?.chains?.[0]?.chainName}
          </Typography>
        </Stack>
      </Stack>
      <Stack sx={{ pt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('nftSettingNote')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}

type ItemImageListCellProps = {
  artistId: string
  loading: boolean
  hasNextPage: boolean
  collectionItems: Maybe<ConsoleCollectionItemsConnectionFragment>
  onFetchMore: Action['onFetchMore']
  onRemoveItemFromCollection: Action['onRemoveItemFromCollection']
  onToggleActive: Action['onToggleActive']
  onToggleSelling: Action['onToggleSelling']
}
const ItemImageListCell: Cell<ItemImageListCellProps> = ({
  artistId,
  loading,
  hasNextPage,
  collectionItems,
  onFetchMore,
  onRemoveItemFromCollection,
  onToggleActive,
  onToggleSelling,
}) => {
  const { t } = useTranslation('consoleCollectionDetail')
  return (
    <CellFrame sx={{ px: 5 }}>
      {loading && !collectionItems?.edges ? (
        <SkeletonText variant="large" width="50%" sx={{ mt: 1, mb: 3 }} />
      ) : (
        <Typography variant="h4" sx={{ pt: 1, pb: 3 }}>
          {t('registeredItem')}
        </Typography>
      )}
      <ScrollFetcher
        loading={loading}
        hasNextPage={hasNextPage}
        emptyMessage={t('emptyItemMessage')}
        nodes={collectionItems?.edges.map(({ node }) => (
          <ItemSelectBox
            artistId={artistId}
            item={node.item}
            showIcon
            editable={false}
            onToggleActive={onToggleActive}
            onToggleSelling={onToggleSelling}
            onRemove={onRemoveItemFromCollection}
            menuLabel={{
              deleteLabel: t(
                'deleteItemFormCollection',
                'コレクションから除外'
              ),
            }}
            removeItemConfirmModal={{
              title: t('deleteItemFromCollectionModalTitle'),
              content: t('deleteItemFromCollectionModalContent'),
              confirm: t('deleteItem'),
            }}
          />
        ))}
        onFetchMore={onFetchMore}
        loadingComponent={
          <Stack spacing={3} sx={{ pt: 3, pb: 5 }}>
            {Array.from(Array(3).keys()).map(key => (
              <ItemSelectBoxSkeleton key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}

type BaseCellProps = Omit<ItemSelectModalProps, 'open' | 'onClose'> & {
  nft: ConsoleCollectionDetailFragment['nft']
  hasCryptoWallet: boolean
  onConnectWallet: CryptoWalletConnectDrawerProps['onConnectWallet']
}
const BaseCell: Cell<BaseCellProps> = props => {
  const [openItemSelect, setOpenItemSelect] = useState<boolean>(false)
  const [openWalletConnect, setOpenWalletConnect] = useState<boolean>(false)

  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          onClick={async () => {
            if (props.nft) {
              // ウォレットの連携
              if (!props.hasCryptoWallet) {
                setOpenWalletConnect(true)
                return
              }
            }

            setOpenItemSelect(true)
            await props.onSearch({ keyword: '' })
            return
          }}
        >
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
      <ItemSelectModal
        open={openItemSelect}
        onClose={() => setOpenItemSelect(false)}
        {...props}
        onSelect={async itemId => {
          await props.onSelect(itemId)
          setOpenItemSelect(false)
        }}
      />
      <CryptoWalletConnectDrawer
        open={openWalletConnect}
        onClose={() => setOpenWalletConnect(false)}
        connecting={props.walletConnecting}
        onConnectWallet={props.onConnectWallet}
      />
    </Stack>
  )
}

const CollectionDetailLoading: Cell = () => (
  <CellFrame>
    <Skeleton
      variant="rectangular"
      width="100%"
      sx={{
        height: 'calc(100vw *  212 / 375)',
        maxHeight: 'calc(600px * 212 / 375)',
      }}
    />
    <Stack sx={{ p: 5 }}>
      <SkeletonText variant="small" width="25%" />
      <SkeletonText variant="large" width="75%" />
      <SkeletonText variant="medium" width="90%" />
      <SkeletonText variant="medium" width="75%" />
    </Stack>
    <Stack sx={{ p: 5 }}>
      <SkeletonText variant="large" width="50%" sx={{ mt: 1, mb: 3 }} />
    </Stack>
  </CellFrame>
)
